import { ViewTransition } from 'react';
import React from 'react';
// Prefetched on intent, not on sight (components/IntentLink.tsx).
import Link from '@/components/IntentLink';
import { BedDouble, Bath, CameraOff } from 'lucide-react';
import CardPhotoCarousel from './CardPhotoCarousel';
import FavoriteButton from './FavoriteButton';
import { formatPrice } from '@/lib/format';
import { isRental, perMonth } from '@/lib/rental-terms';
import { propertyTypeLabel } from '@/lib/property-types';
import { badgeLabel, cardBadge, listedAgo, listedAt, sellerLine, roomCount, areaLabel, type CardBadge } from '@/lib/listing-card';
import { listingPathFor } from '@/lib/place-url';

// One listing in a list of results - the same card on the home page, a city,
// an agency site and the saved list (listing browse plan, 2026-09-23).
//
// On the photo: the heart, at most one badge, and the photo counter. Under it,
// always in this order: the price, one line of specs, the address, and one
// grey line of when and who. Nothing else competes for the eye.
//
// The heading's link is the card's one link for keyboards and screen readers;
// its ::after covers the text part, and the photos carry their own links for a
// tap, so a sideways swipe on them still scrolls the photos.

// The colours live in site.css (photo-badge-*), with the rest of the site's.
const BADGE_STYLE: Record<CardBadge, string> = {
  sold: 'photo-badge-sold',
  reduced: 'photo-badge-reduced',
  new: 'photo-badge-new',
};

export default function ListingCard({
  property,
  lang,
  now,
  eager = false,
  hideSeller = false,
  singlePhoto = false,
  className = '',
}: {
  property: any;
  lang: string;
  /** The clock read once for the whole list, so every card is judged alike. */
  now: number;
  eager?: boolean;
  /** On an agency's own site every listing is theirs. */
  hideSeller?: boolean;
  /**
   * One photo, no swiping: in a row that itself scrolls sideways, a swipe on
   * a photo would fight the swipe of the row.
   */
  singlePhoto?: boolean;
  className?: string;
}) {
  const isFr = lang === 'fr';
  const title = (isFr && property.title_fr ? property.title_fr : property.title_en) || '';
  const href = listingPathFor(lang, property);
  const images: string[] =
    Array.isArray(property.images) && property.images.length > 0
      ? property.images
      : Array.isArray(property.photos) ? property.photos : [];

  const badge = cardBadge(property, now);
  const rent = isRental(property);
  const beds = roomCount(property.bedrooms);
  const baths = roomCount(property.bathrooms);
  const area = areaLabel(property.living_area, lang);
  const type = propertyTypeLabel(property.property_type, lang);
  const place = `${property.address || title}${property.address || title ? ', ' : ''}${property.city}`;
  const when = listedAgo(listedAt(property), lang, now);
  const meta = [when, hideSeller ? '' : sellerLine(property, lang)].filter(Boolean).join(' · ');

  const specs: React.ReactNode[] = [];
  if (type) specs.push(<span key="type">{type}</span>);
  if (beds !== null) specs.push(
    <span key="beds" className="inline-flex items-center gap-1">
      <BedDouble aria-hidden="true" className="w-4 h-4 text-neutral-400" />
      {beds}
      <span className="sr-only">{isFr ? (beds > 1 ? ' chambres' : ' chambre') : (beds === 1 ? ' bedroom' : ' bedrooms')}</span>
    </span>
  );
  if (baths !== null) specs.push(
    <span key="baths" className="inline-flex items-center gap-1">
      <Bath aria-hidden="true" className="w-4 h-4 text-neutral-400" />
      {baths}
      <span className="sr-only">{isFr ? (baths > 1 ? ' salles de bain' : ' salle de bain') : (baths === 1 ? ' bathroom' : ' bathrooms')}</span>
    </span>
  );
  if (area) specs.push(<span key="area">{area}</span>);

  return (
    <article
      id={`listing-${property.id}`}
      className={`${className}group relative flex flex-col bg-neutral-900 border border-neutral-800 rounded-2xl overflow-hidden transition-colors pointer-fine:hover:border-neutral-600 has-[h3_a:focus-visible]:ring-2 has-[h3_a:focus-visible]:ring-gold`}
    >
      <div className="relative aspect-[3/2] bg-neutral-800">
        {images.length > 0 ? (
          <ViewTransition name={`photo-${property.id}`} share="morph" default="none">
            <CardPhotoCarousel images={singlePhoto ? images.slice(0, 1) : images} total={singlePhoto ? 1 : property.photo_count} title={title} href={href} lang={lang} eager={eager} />
          </ViewTransition>
        ) : (
          <Link href={href} tabIndex={-1} aria-hidden="true" className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-neutral-400">
            <CameraOff className="w-7 h-7" />
            <span className="text-xs font-medium">{isFr ? 'Aucune photo' : 'No photos yet'}</span>
          </Link>
        )}
        {badge && (
          <span className={`photo-badge ${BADGE_STYLE[badge]}`}>
            {badgeLabel(badge, lang, rent)}
          </span>
        )}
        <FavoriteButton propertyId={property.id} lang={lang} />
      </div>

      {/* The heading comes first for a screen reader moving by headings; the
          eye gets the price first. */}
      <div className="relative flex flex-col gap-1 p-4">
        <h3 className="order-3 text-sm text-neutral-300 line-clamp-2 break-words">
          <Link href={href} className="after:absolute after:inset-0 after:content-[''] focus-visible:outline-none!">
            {place}
          </Link>
        </h3>
        <p className="order-1 flex items-baseline gap-2 min-w-0">
          <span className="text-xl font-bold text-white">
            {formatPrice(property.price, lang)}
            {rent && Number(property.price) > 0 && <span className="text-sm font-semibold text-neutral-400">{perMonth(lang)}</span>}
          </span>
          {badge === 'reduced' && (
            <>
              <span className="sr-only">{isFr ? 'au lieu de' : 'was'}</span>
              <s className="text-sm text-neutral-400 truncate">{formatPrice(property.price_reduced_from, lang)}</s>
            </>
          )}
        </p>
        {specs.length > 0 && (
          <p className="order-2 flex flex-wrap items-center gap-x-2 text-sm text-neutral-200">
            {specs.map((item, i) => (
              <React.Fragment key={i}>
                {i > 0 && <span aria-hidden="true" className="text-neutral-400">·</span>}
                {item}
              </React.Fragment>
            ))}
          </p>
        )}
        {meta && <p className="order-4 text-xs text-neutral-400 truncate">{meta}</p>}
      </div>
    </article>
  );
}
